import $ from 'jquery';

/**
 * @typedef Popup
 * @prop {string} name
 * @prop {string|(() => boolean)} visible
 * @prop {(() => void)=} show
 * @prop {(() => void)=} hide
 */

const modalPriorities = [
  'Quick start guide',
  'Quick start step',
  'Extension permissions',
  'Broadcasts',
];

/**
 * @typedef PopupDisplayOptions
 * @prop {boolean=} checkPriority defaults true
 */

/**
 * A priority service for popups that need to be displayed one at a time. Popups
 * can be anything that can be hidden and shown. Register each popup with a name.
 *
 * @example
 *
 *   const popup = PopupPriority.register({
 *     name: 'My popup',
 *     show() { $('#popup').removeClass('hide'); },
 *     hide() { $('#popup').addClass('hide'); },
 *     visible() { return $('#popup').hasClass('hide') }
 *   })
 *   popup.canDisplay() // only true if no higher priority popups are displayed
 *   popup.show() // will hide any lower priority popups
 */
class PopupPriorityService {
  constructor() {
    /** @type {Map<string, Popup>} */
    this.popups = new Map();
  }

  /**
   * @param {Popup} popup
   */
  register(popup) {
    // If there's already a popup with this name hide it
    this.hide(popup.name);

    this.popups.set(popup.name, popup);
    return {
      ...popup,
      /**
       * @param {PopupDisplayOptions=} options
       */
      show(options = {}) {
        return PopupPriority.show(popup.name, options);
      },
      hide() {
        return PopupPriority.hide(popup.name);
      },
      /**
       * @param {PopupDisplayOptions=} options
       */
      toggle(options = {}) {
        return PopupPriority.toggle(popup.name, options);
      },
      visible() {
        return PopupPriority.visible(popup.name);
      },
      canDisplay() {
        return PopupPriority.canDisplay(popup.name);
      },
      willDisplay() {
        return PopupPriority.willDisplay(popup.name);
      },
    };
  }

  visible(name) {
    const popup = this.popups.get(name);
    if (!popup) return false;

    if (typeof popup.visible === 'function') return popup.visible();

    return $(popup.visible).length > 0;
  }

  canDisplay(name) {
    for (const other of modalPriorities) {
      if (other === name) return true;
      if (this.visible(other)) {
        console.log('cannot open', name, 'because', other, 'is open');
        return false;
      }
    }

    return true;
  }

  willDisplay(name) {
    [...this.popups.values()]
      .filter(popup => popup.name !== name && this.visible(popup.name))
      .forEach(popup => {
        this.hide(popup.name);
      });
  }

  /**
   * @param {string} name
   * @param {PopupDisplayOptions} options
   */
  show(name, options = {}) {
    const { checkPriority = true } = options;
    if (checkPriority && !this.canDisplay(name)) return false;

    const popup = this.popups.get(name);
    if (!popup?.show) return false;

    this.willDisplay(name);
    popup.show();
    return true;
  }

  hide(name) {
    const popup = this.popups.get(name);
    if (!popup?.hide) return false;

    popup.hide();
    return true;
  }

  /**
   *
   * @param {string} name
   * @param {PopupDisplayOptions} options
   */
  toggle(name, options = {}) {
    if (this.visible(name)) return this.hide(name);
    return this.show(name, options);
  }
}
const PopupPriority = new PopupPriorityService();

PopupPriority.register({
  name: 'other',
  visible: '.modal.in',
});

/**
 * Register a static modal as a priority popup. A static modal is one rendered
 * on the screen that can be opened and closed.
 *
 * @example
 *
 *   const modal = registerModal('Quick start guide', '#quick_start_guide');
 *   modal.show();
 *   PopupPriority.hide('Quick start guide');
 *
 * @param {string} name
 * @param {string} selector
 */
function registerModal(name, selector) {
  let shown = false;
  $(document).on('show.popup_priority', selector, () => {
    shown = true;
  });
  $(document).on('hide.popup_priority', selector, () => {
    shown = false;
  });
  $(document).on('page:change', () => {
    shown = false;
  });

  const modal = PopupPriority.register({
    name,
    visible: () => shown,
    show: () => {
      $(selector).modal();
    },
    hide: () => {
      $(selector).modal('hide');
    },
  });

  modal.find = subSelector => $(selector).find(subSelector);

  return modal;
}

/**
 * Register a rails driven modal in the popup priorities. Pass the name and
 * content. This type of modal cannot be shown again once closed.
 *
 * @example
 *   registerRailsModal('My modal', <%= raw render('modal').to_json %>).show();
 *
 * @param {string} name
 * @param {string} content
 */
function registerRailsModal(name, content) {
  let shown = true;
  $(document).one('hide.popup_priority', '#modal-window', () => {
    shown = false;
  });
  $(document).one('page:change', '#modal-window', () => {
    shown = false;
  });

  return PopupPriority.register({
    name,
    visible() {
      return shown;
    },
    show() {
      // This is server-rendered HTML.
      // eslint-disable-next-line no-unsanitized/method
      $('#modal-window').addClass('modal-big').html(content).modal('show');
    },
    hide() {
      if (!shown) $('#modal-window').modal('hide');
    },
  });
}

export { PopupPriority, registerModal, registerRailsModal };
